import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Input, Textarea } from '@nexora/design-system';
import { useToast } from '@nexora/design-system';
import { formatCurrency } from '../../shared/utils/format';
import { apiClient } from '../../shared/api/client';
import { useAuth } from '../../shared/auth/AuthContext';
import { useCustomers } from '../master-data/useMasterData';
import { useQuotes } from '../sales/useQuotes';
import { useCreateOpportunity, type OpportunityStage } from './useOpportunities';

/* ── Porte de `erpone-nova-oportunidade-de-venda` ─────────────────────────
   Cria Opportunity (RF-002) — nunca Quote como substituto do deal.
   Dono = usuário da sessão. Painel de contexto com orçamentos e pedidos
   reais do cliente (o guia mostra histórico; Interação ainda não tem API).
   "Produtos de interesse" não tem fonte no backend e ficou de fora. */

const STAGES: { id: OpportunityStage; label: string }[] = [
  { id: 'PROSPECTING', label: 'Prospecção' },
  { id: 'QUALIFICATION', label: 'Qualificação' },
  { id: 'PROPOSAL', label: 'Proposta' },
  { id: 'NEGOTIATION', label: 'Negociação' },
];

interface OrderRow {
  id: string;
  grandTotal: number;
  status: string;
  createdAt: string;
}

export function NewOpportunityPage(): JSX.Element {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user } = useAuth();
  const createOpportunity = useCreateOpportunity();
  const customersQuery = useCustomers();

  const [title, setTitle] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [buscaCliente, setBuscaCliente] = useState('');
  const [amount, setAmount] = useState('');
  const [expectedCloseDate, setExpectedCloseDate] = useState('');
  const [stage, setStage] = useState<OpportunityStage>('PROSPECTING');
  const [probability, setProbability] = useState(10);
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<string[]>([]);

  const customers = useMemo(() => customersQuery.data ?? [], [customersQuery.data]);
  const cliente = customers.find((c) => c.id === customerId);

  const sugestoes = useMemo(() => {
    const termo = buscaCliente.trim().toLowerCase();
    if (termo.length < 2 || cliente) return [];
    return customers
      .filter(
        (c) =>
          c.name.toLowerCase().includes(termo) ||
          (c.documentNumber ?? '').includes(termo),
      )
      .slice(0, 8);
  }, [buscaCliente, customers, cliente]);

  const quotesQuery = useQuotes({ customerId: customerId || undefined, limit: 5 });
  const ordersQuery = useQuery<{ data: OrderRow[] }>({
    queryKey: ['sales', 'orders', 'customer', customerId],
    queryFn: async () =>
      apiClient.get<{ data: OrderRow[] }>('/orders', {
        params: { customerId, page: '1', limit: '5' },
      }),
    enabled: !!customerId,
  });

  const submit = (): void => {
    const errs: string[] = [];
    if (!title.trim()) errs.push('Nome da oportunidade é obrigatório.');
    if (!customerId) errs.push('Cliente associado é obrigatório.');
    const value = Number(amount);
    if (!Number.isFinite(value) || value < 0) errs.push('Valor previsto deve ser ≥ 0.');
    if (!user) errs.push('Sessão expirada — entre novamente.');
    setErrors(errs);
    if (errs.length > 0 || !user) return;

    createOpportunity.mutate(
      {
        customerId,
        title: title.trim(),
        amount: value,
        ownerId: user.id,
        stage,
        probability,
        ...(expectedCloseDate ? { expectedCloseDate } : {}),
        ...(description.trim() ? { description: description.trim() } : {}),
      },
      {
        onSuccess: (opp) => {
          addToast(`Oportunidade criada (${opp.stage}).`, 'success');
          void navigate({ to: '/crm/pipeline' });
        },
        onError: (err) => {
          addToast(`Falha ao criar: ${(err as Error).message}`, 'error');
        },
      },
    );
  };

  return (
    <div className="p-xl">
      <div className="mb-lg flex flex-col gap-md sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="m-0 font-display-lg text-display-lg text-on-surface">
            Nova Oportunidade
          </h1>
          <p className="mt-xs font-body-md text-body-md text-on-surface-variant">
            Cria um deal no funil — sem virar orçamento à força.
          </p>
        </div>
        <div className="flex gap-sm">
          <Button variant="outline" onClick={() => void navigate({ to: '/crm/pipeline' })}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={createOpportunity.isPending}
            onClick={submit}
          >
            Criar Oportunidade
          </Button>
        </div>
      </div>

      {errors.length > 0 && (
        <Card>
          <ul className="m-0 p-0" style={{ listStyle: 'none' }}>
            {errors.map((e) => (
              <li key={e} className="font-body-md text-body-md text-error">
                {e}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-lg xl:grid-cols-2">
        {/* Detalhes principais */}
        <Card>
          <h2 className="mb-md mt-0 font-headline-sm text-headline-sm text-on-surface">
            Detalhes Principais
          </h2>
          <div className="flex flex-col gap-md">
            <Input
              id="opp-title"
              label="Nome da Oportunidade *"
              placeholder="Ex.: Implantação ERP Fiscal"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <div>
              <div className="field-label">Cliente Associado (Lookup) *</div>
              {cliente ? (
                <div className="flex items-center justify-between rounded-md border border-outline-variant bg-surface-container-low px-sm py-xs">
                  <span className="font-body-md text-body-md text-on-surface">
                    {cliente.name}
                  </span>
                  <button
                    type="button"
                    className="font-label-md text-label-md text-primary hover:underline"
                    onClick={() => {
                      setCustomerId('');
                      setBuscaCliente('');
                    }}
                  >
                    Trocar
                  </button>
                </div>
              ) : (
                <>
                  <Input
                    id="opp-customer"
                    placeholder="Buscar por nome ou documento"
                    value={buscaCliente}
                    onChange={(e) => setBuscaCliente(e.target.value)}
                  />
                  {sugestoes.length > 0 && (
                    <ul
                      className="mt-xs overflow-hidden rounded-md border border-outline-variant bg-surface-container-lowest"
                      style={{ listStyle: 'none', margin: 0, padding: 0 }}
                    >
                      {sugestoes.map((c) => (
                        <li key={c.id}>
                          <button
                            type="button"
                            className="w-full px-sm py-xs text-left font-body-md text-body-md text-on-surface hover:bg-surface-container-low"
                            onClick={() => {
                              setCustomerId(c.id);
                              setBuscaCliente('');
                            }}
                          >
                            {c.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
            <div className="grid grid-cols-1 gap-md sm:grid-cols-2">
              <Input
                id="opp-amount"
                label="Valor Previsto (R$)"
                type="number"
                min="0"
                step="0.01"
                placeholder="0,00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              <Input
                id="opp-close-date"
                label="Data Fechamento Prevista"
                type="date"
                value={expectedCloseDate}
                onChange={(e) => setExpectedCloseDate(e.target.value)}
              />
            </div>
            <div>
              <div className="field-label">Fase do Funil</div>
              <div className="flex flex-wrap gap-xs">
                {STAGES.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setStage(s.id)}
                    aria-pressed={stage === s.id}
                    className={`rounded-md border px-sm py-xs font-label-md text-label-md transition-colors ${
                      stage === s.id
                        ? 'border-primary bg-primary-container font-semibold text-on-primary-container'
                        : 'border-outline-variant text-on-surface-variant hover:bg-surface-container-low'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="field-label">Probabilidade (%) — {probability}%</div>
              <input
                id="opp-probability"
                type="range"
                min={0}
                max={100}
                step={5}
                value={probability}
                onChange={(e) => setProbability(Number(e.target.value))}
                className="w-full"
                aria-label="Probabilidade em percentual"
              />
            </div>
            <Textarea
              id="opp-description"
              label="Descrição e Contexto"
              placeholder="Contexto do deal, decisores, próximos passos…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </Card>

        {/* Contexto do cliente */}
        <Card>
          <h2 className="mb-md mt-0 font-headline-sm text-headline-sm text-on-surface">
            Contexto do Cliente
          </h2>
          {!cliente ? (
            <p className="m-0 font-body-md text-body-md text-on-surface-variant">
              Selecione um cliente para ver o histórico recente.
            </p>
          ) : (
            <div className="flex flex-col gap-md">
              <div>
                <p className="mb-xs mt-0 font-label-md text-label-md text-on-surface-variant">
                  ORÇAMENTOS RECENTES
                </p>
                {(quotesQuery.data?.data ?? []).length === 0 ? (
                  <p className="m-0 font-body-sm text-body-sm text-on-surface-variant">
                    Nenhum orçamento para {cliente.name}.
                  </p>
                ) : (
                  <ul className="m-0 flex flex-col gap-xs p-0" style={{ listStyle: 'none' }}>
                    {quotesQuery.data!.data.slice(0, 5).map((q) => (
                      <li
                        key={q.id}
                        className="flex items-center justify-between rounded-md border border-outline-variant px-sm py-xs"
                      >
                        <span className="font-body-sm text-body-sm text-on-surface">
                          {q.status} · {new Date(q.createdAt).toLocaleDateString('pt-BR')}
                        </span>
                        <span className="font-label-md text-label-md text-on-surface">
                          {formatCurrency(q.grandTotal)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <p className="mb-xs mt-0 font-label-md text-label-md text-on-surface-variant">
                  PEDIDOS
                </p>
                {(ordersQuery.data?.data ?? []).length === 0 ? (
                  <p className="m-0 font-body-sm text-body-sm text-on-surface-variant">
                    Nenhum pedido para {cliente.name}.
                  </p>
                ) : (
                  <ul className="m-0 flex flex-col gap-xs p-0" style={{ listStyle: 'none' }}>
                    {ordersQuery.data!.data.slice(0, 5).map((o) => (
                      <li
                        key={o.id}
                        className="flex items-center justify-between rounded-md border border-outline-variant px-sm py-xs"
                      >
                        <span className="font-body-sm text-body-sm text-on-surface">
                          {o.status}
                        </span>
                        <span className="font-label-md text-label-md text-on-surface">
                          {formatCurrency(o.grandTotal)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
